import React, { useState } from 'react';
import { EmptyState } from '../common';
import { AUDIT_ACTIONS, AUDIT_ENTITY_TYPES } from '../../constants/admin';
import api from '../../services/api';

const AdminAuditLogsTab = ({
  logs,
  loading,
  filters,
  setFilters,
  onSearch
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (logId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedRows(newExpanded);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.action) params.append('action', filters.action);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);

      const response = await api.get(`/admin/audit-logs/export?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'audit_logs_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatJson = (obj) => {
    if (!obj) return 'N/A';
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  const getActionBadgeClass = (action) => {
    switch (action) {
      case 'create': return 'status-badge active';
      case 'update': return 'status-badge pending';
      case 'delete': return 'status-badge cancelled';
      default: return 'status-badge';
    }
  };

  const renderContent = () => {
    if (loading) {
      return <div className="loading">Loading...</div>;
    }

    if (logs.length === 0) {
      return (
        <EmptyState
          icon="📋"
          title="No audit logs found"
          message={filters.action || filters.entity_type
            ? "No logs match your current filters. Try adjusting your search criteria."
            : "No admin actions have been logged yet."}
        />
      );
    }

    return (
      <div className="admin-table-wrapper">
        <table className="admin-table audit-logs-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th>Timestamp</th>
              <th>User</th>
              <th>Action</th>
              <th>Entity Type</th>
              <th>Entity ID</th>
              <th>IP Address</th>
            </tr>
          </thead>
          <tbody>
            {logs.map(log => (
              <React.Fragment key={log.id}>
                <tr
                  onClick={() => toggleRow(log.id)}
                  className={expandedRows.has(log.id) ? 'expanded' : ''}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="expand-cell">
                    <span className="expand-icon">
                      {expandedRows.has(log.id) ? '▼' : '▶'}
                    </span>
                  </td>
                  <td>{formatDate(log.created_at)}</td>
                  <td>
                    <div>{log.user_name || 'Unknown'}</div>
                    <small className="text-muted">{log.user_email}</small>
                  </td>
                  <td>
                    <span className={getActionBadgeClass(log.action)}>
                      {log.action}
                    </span>
                  </td>
                  <td>{log.entity_type}</td>
                  <td>{log.entity_id || '-'}</td>
                  <td>{log.ip_address || '-'}</td>
                </tr>
                {expandedRows.has(log.id) && (
                  <tr className="expanded-row">
                    <td colSpan="7">
                      <div className="audit-details">
                        <div className="audit-detail-section">
                          <h4>User Agent</h4>
                          <p className="user-agent">{log.user_agent || 'N/A'}</p>
                        </div>
                        <div className="audit-values-grid">
                          <div className="audit-detail-section">
                            <h4>Old Values</h4>
                            <pre className="json-display">{formatJson(log.old_values)}</pre>
                          </div>
                          <div className="audit-detail-section">
                            <h4>New Values</h4>
                            <pre className="json-display">{formatJson(log.new_values)}</pre>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <div className="admin-actions-bar">
        <div className="admin-filters audit-filters">
          <select
            value={filters.action || ''}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          >
            <option value="">All Actions</option>
            {AUDIT_ACTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={filters.entity_type || ''}
            onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
          >
            <option value="">All Entity Types</option>
            {AUDIT_ENTITY_TYPES.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button className="btn-search" onClick={onSearch}>
            Search
          </button>
        </div>
        <button className="btn-export" onClick={handleExport}>
          Export CSV
        </button>
      </div>
      {renderContent()}
    </>
  );
};

export default AdminAuditLogsTab;
